import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Match } from "@/types";
import { teamMap } from "@/mocks/teams";
import TeamLogo from "@/components/base/TeamLogo";
import { formatTime, formatDateLabel } from "@/utils/format";

interface Slide {
  match: Match;
  image: string;
  tagline: string;
}

const SLIDE_IMAGES = [
  "https://readdy.ai/api/search-image?query=Abstract%20dramatic%20basketball%20arena%20with%20spotlights%20and%20blue%20orange%20light%20beams%2C%20dark%20moody%20atmosphere%2C%20stylized%20artistic%20sports%20background%2C%20no%20text%2C%20cinematic&width=1600&height=800&seq=nba-hero-01&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Stylized%20abstract%20basketball%20court%20with%20glowing%20orange%20rim%20and%20deep%20blue%20gradient%20lighting%2C%20dark%20wallpaper%20style%2C%20no%20text%2C%20cinematic%20sports%20background&width=1600&height=800&seq=nba-hero-02&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Dynamic%20abstract%20basketball%20motion%20trails%20in%20dark%20arena%2C%20electric%20blue%20and%20orange%20glow%2C%20stylized%20artistic%20sports%20poster%20background%2C%20no%20text%2C%20cinematic&width=1600&height=800&seq=nba-hero-03&orientation=landscape",
];

interface HeroCarouselProps {
  matches: Match[];
}

export default function HeroCarousel({ matches }: HeroCarouselProps) {
  const slides: Slide[] = matches.slice(0, 3).map((match, i) => ({
    match,
    image: SLIDE_IMAGES[i % SLIDE_IMAGES.length],
    tagline:
      match.status === "live"
        ? "正在直播"
        : match.status === "replay"
          ? "精彩回放"
          : "即将开赛",
  }));

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => setIndex((v) => (v + 1) % slides.length), 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) {
    return (
      <section className="relative w-full overflow-hidden rounded-lg bg-primary-900 px-6 py-16 text-center text-background-50">
        <div className="flex w-full flex-col items-center">
          <i className="ri-basketball-line text-5xl" />
          <h2 className="mt-4 font-heading text-2xl font-bold">暂无直播赛事</h2>
          <p className="mt-2 text-sm text-background-300">当前没有正在进行的比赛，去看看接下来的赛程吧</p>
          <Link
            to="/schedule"
            className="mt-6 inline-flex items-center gap-1 rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap"
          >
            <i className="ri-calendar-line" /> 查看赛程
          </Link>
        </div>
      </section>
    );
  }

  const active = slides[index];
  const home = teamMap[active.match.homeTeamId];
  const away = teamMap[active.match.awayTeamId];

  return (
    <section className="relative w-full overflow-hidden rounded-lg">
      <div className="relative h-[360px] md:h-[480px] w-full">
        {slides.map((slide, i) => (
          <img
            key={slide.match.id}
            src={slide.image}
            alt="NBA赛事直播焦点横幅"
            title="NBA篮球赛事直播焦点"
            className={`absolute inset-0 h-full w-full object-top transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/85 via-background-950/60 to-background-950/25" />

        <div className="relative flex h-full w-full flex-col justify-center px-6 md:px-12">
          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-fire-fill" /> {active.tagline}
          </span>
          <p className="mt-4 text-sm text-background-200">
            {formatDateLabel(active.match.startTime)} {formatTime(active.match.startTime)} · {active.match.stage}
          </p>
          <h2 className="mt-2 font-heading text-3xl font-bold text-background-50 md:text-5xl">
            {home?.city}{home?.name} <span className="text-accent-400">VS</span> {away?.city}{away?.name}
          </h2>

          <div className="mt-4 flex items-center gap-4">
            {home && <TeamLogo team={home} size="md" />}
            <span className="font-heading text-2xl font-bold text-background-50">
              {active.match.status === "upcoming" ? "—" : `${active.match.homeScore} : ${active.match.awayScore}`}
            </span>
            {away && <TeamLogo team={away} size="md" />}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/match/${active.match.id}`}
              className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-circle-line text-lg" /> 立即观看
            </Link>
            <Link
              to={`/team/${active.match.homeTeamId}`}
              className="inline-flex items-center gap-2 rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-background-50/10"
            >
              <i className="ri-team-line" /> 球队资料
            </Link>
          </div>
        </div>

        {slides.length > 1 && (
          <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.match.id}
                type="button"
                aria-label={`切换到第 ${i + 1} 张`}
                onClick={() => setIndex(i)}
                className={`h-2 rounded-full transition-all ${i === index ? "w-7 bg-accent-500" : "w-2 bg-background-50/60"}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}